import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryCalendar as calApi } from '@/utils/api';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Briefcase, DollarSign,
  Flag, RefreshCw, AlertTriangle, CheckCircle2, Circle, X,
} from 'lucide-react';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const TYPE_META = {
  project:   { icon: Briefcase, color: '#006080', label: 'Project' },
  invoice:   { icon: DollarSign, color: '#D9A441', label: 'Invoice' },
  milestone: { icon: Flag, color: '#5B2D8E', label: 'Milestone' },
};

const HEALTH_COLOR = {
  overdue:  '#B34A3A',
  at_risk:  '#D9A441',
  on_track: '#3C8A5A',
  done:     '#8A99A0',
};

export default function DeliveryCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based
  const [filters, setFilters] = useState({ project: true, invoice: true, milestone: true });
  const [data, setData] = useState({ items: [], counts_by_day: {}, today: today.toISOString().slice(0, 10) });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const include = Object.entries(filters).filter(([, v]) => v).map(([k]) => k === 'project' ? 'projects' : k === 'invoice' ? 'invoices' : 'milestones').join(',');
      const r = await calApi.get(year, month, include || 'none');
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, filters]);

  // Build 6-row grid of dates (Sun-start)
  const grid = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startOffset = first.getDay(); // 0 = Sun
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    // Previous-month filler
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, -i);
      cells.push({ date: d, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(year, month - 1, d), inMonth: true });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const last = cells[cells.length - 1].date;
      cells.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), inMonth: false });
    }
    return cells.slice(0, 42);
  }, [year, month]);

  const isoOf = (d) => d.toISOString().slice(0, 10);
  const itemsByDay = useMemo(() => {
    const map = {};
    for (const it of data.items) {
      if (!map[it.date]) map[it.date] = [];
      map[it.date].push(it);
    }
    return map;
  }, [data.items]);

  const goto = (deltaMonths) => {
    let m = month + deltaMonths;
    let y = year;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    setYear(y); setMonth(m); setSelectedDay(null);
  };
  const gotoToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); setSelectedDay(null); };

  // Overdue items in the current view (for the alert banner)
  const overdueCount = data.items.filter(i => i.health === 'overdue').length;
  const todayStr = data.today;

  return (
    <div className="p-6 animate-fade-in max-w-[1400px] mx-auto" data-testid="delivery-calendar-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-btn flex items-center justify-center" style={{ background: '#5B2D8E15' }}>
            <CalendarIcon size={18} style={{ color: '#5B2D8E' }} />
          </div>
          <div>
            <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Delivery Calendar</h1>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>Month-at-a-glance of what's due when — projects, invoices, and milestones</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filters */}
          {[
            { key: 'project',   label: 'Projects',   meta: TYPE_META.project },
            { key: 'invoice',   label: 'Invoices',   meta: TYPE_META.invoice },
            { key: 'milestone', label: 'Milestones', meta: TYPE_META.milestone },
          ].map(({ key, label, meta }) => {
            const Icon = meta.icon;
            const on = filters[key];
            return (
              <label
                key={key}
                className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded-btn font-semibold cursor-pointer select-none"
                style={{
                  background: on ? `${meta.color}15` : 'var(--bg)',
                  color: on ? meta.color : 'var(--muted)',
                  border: `1px solid ${on ? meta.color + '40' : 'var(--border)'}`,
                }}
                data-testid={`filter-${key}`}
              >
                <input type="checkbox" checked={on} onChange={(e) => setFilters(f => ({ ...f, [key]: e.target.checked }))} className="sr-only" />
                <Icon size={11} /> {label}
                <span className="font-mono text-[10px] opacity-70">·{data.items.filter(i => i.type === key).length}</span>
              </label>
            );
          })}
          <button onClick={load} className="p-1.5 rounded-btn" style={{ color: 'var(--muted)' }} title="Refresh" data-testid="calendar-refresh">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {overdueCount > 0 && (
        <div className="card-base p-3 mb-4 flex items-center gap-2" style={{ borderLeft: '3px solid #B34A3A' }} data-testid="overdue-banner">
          <AlertTriangle size={14} style={{ color: '#B34A3A' }} />
          <p className="text-sm" style={{ color: 'var(--ink)' }}>
            <strong>{overdueCount}</strong> overdue {overdueCount === 1 ? 'item' : 'items'} this month. Scroll down or click a red day to address.
          </p>
        </div>
      )}

      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          <button onClick={() => goto(-1)} className="p-1.5 rounded-btn" style={{ color: 'var(--ink)' }} data-testid="prev-month"><ChevronLeft size={16} /></button>
          <h2 className="text-lg font-black px-2" style={{ color: 'var(--ink)' }} data-testid="month-label">
            {MONTH_LABELS[month - 1]} {year}
          </h2>
          <button onClick={() => goto(1)} className="p-1.5 rounded-btn" style={{ color: 'var(--ink)' }} data-testid="next-month"><ChevronRight size={16} /></button>
        </div>
        <button onClick={gotoToday} className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }} data-testid="goto-today">
          Today
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-0 rounded-card overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }} data-testid="calendar-grid">
        {DAY_LABELS.map(d => (
          <div key={d} className="p-2 text-[10px] font-bold uppercase tracking-wider text-center" style={{ color: 'var(--muted)', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
            {d}
          </div>
        ))}
        {grid.map((c, i) => {
          const iso = isoOf(c.date);
          const dayItems = itemsByDay[iso] || [];
          const isToday = iso === todayStr;
          const isSelected = selectedDay === iso;
          const dayCounts = data.counts_by_day[iso];
          return (
            <button
              key={i}
              onClick={() => dayItems.length > 0 && setSelectedDay(iso)}
              className="min-h-[100px] p-2 text-left transition-colors align-top flex flex-col relative"
              style={{
                background: isSelected ? '#E6F1F4' : c.inMonth ? 'var(--surface)' : 'var(--bg)',
                borderRight: (i % 7 < 6) ? '1px solid var(--border)' : 'none',
                borderBottom: (i < 35) ? '1px solid var(--border)' : 'none',
                cursor: dayItems.length > 0 ? 'pointer' : 'default',
                opacity: c.inMonth ? 1 : 0.45,
              }}
              data-testid={`day-${iso}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-bold ${isToday ? 'rounded-full w-6 h-6 flex items-center justify-center' : ''}`}
                  style={{
                    color: isToday ? 'white' : c.inMonth ? 'var(--ink)' : 'var(--muted)',
                    background: isToday ? '#006080' : 'transparent',
                  }}
                >
                  {c.date.getDate()}
                </span>
                {dayCounts?.overdue > 0 && (
                  <span className="text-[9px] font-bold px-1 rounded-sm" style={{ background: '#B34A3A', color: 'white' }} title={`${dayCounts.overdue} overdue`}>
                    !{dayCounts.overdue}
                  </span>
                )}
              </div>
              <div className="flex-1 space-y-0.5 overflow-hidden">
                {dayItems.slice(0, 3).map(it => {
                  const meta = TYPE_META[it.type];
                  const Icon = meta?.icon || Circle;
                  return (
                    <div key={`${it.id}-${it.type}`} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm truncate"
                      style={{ background: `${HEALTH_COLOR[it.health]}15`, color: HEALTH_COLOR[it.health] }}>
                      <Icon size={8} style={{ flexShrink: 0 }} />
                      <span className="truncate font-semibold">{it.title}</span>
                    </div>
                  );
                })}
                {dayItems.length > 3 && (
                  <div className="text-[10px] font-semibold" style={{ color: 'var(--muted)' }}>+{dayItems.length - 3} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Agenda / selected-day panel */}
      {selectedDay ? (
        <div className="mt-5 card-base p-4" data-testid="day-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black text-base" style={{ color: 'var(--ink)' }}>
              {new Date(selectedDay).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
            </h3>
            <button onClick={() => setSelectedDay(null)} className="p-1" style={{ color: 'var(--muted)' }} data-testid="close-day-panel">
              <X size={15} />
            </button>
          </div>
          <DayItemList items={itemsByDay[selectedDay] || []} navigate={navigate} />
        </div>
      ) : (
        <div className="mt-5">
          <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--muted)' }}>
            Upcoming this month ({data.items.length})
          </h3>
          {data.items.length === 0 ? (
            <div className="card-base p-8 text-center">
              <CalendarIcon size={28} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>Nothing scheduled with the selected filters.</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                Set a <strong>Planned delivery date</strong> on projects to see them here.
              </p>
            </div>
          ) : (
            <div className="card-base p-3">
              <DayItemList items={data.items.slice(0, 20)} navigate={navigate} showDate />
              {data.items.length > 20 && (
                <p className="text-xs text-center pt-3 mt-2" style={{ color: 'var(--muted)', borderTop: '1px solid var(--border)' }}>
                  +{data.items.length - 20} more. Click a day above to drill in.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DayItemList({ items, navigate, showDate = false }) {
  if (!items.length) {
    return <p className="text-sm" style={{ color: 'var(--muted)' }}>Nothing scheduled.</p>;
  }
  return (
    <div className="space-y-1.5">
      {items.map(it => {
        const meta = TYPE_META[it.type];
        const Icon = meta?.icon || Circle;
        const color = HEALTH_COLOR[it.health];
        const canClick = !!it.project_id;
        const onClick = canClick ? () => navigate(`/admin/projects/${it.project_id}`) : undefined;
        const Wrap = canClick ? 'button' : 'div';
        return (
          <Wrap
            key={`${it.type}-${it.id}`}
            onClick={onClick}
            className={`w-full flex items-center gap-2.5 p-2.5 rounded-btn text-left ${canClick ? 'hover:shadow-sm transition-shadow' : ''}`}
            style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
            data-testid={`item-${it.type}-${it.id}`}
          >
            <div className="w-7 h-7 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: `${meta?.color || '#006080'}15` }}>
              <Icon size={12} style={{ color: meta?.color || '#006080' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold truncate" style={{ color: 'var(--ink)' }}>{it.title}</p>
                {it.health === 'overdue' && (
                  <span className="badge-status flex-shrink-0" style={{ background: '#B34A3A15', color: '#B34A3A' }}>Overdue</span>
                )}
                {it.health === 'at_risk' && (
                  <span className="badge-status flex-shrink-0" style={{ background: '#D9A44115', color: '#D9A441' }}>This week</span>
                )}
                {it.health === 'done' && (
                  <CheckCircle2 size={12} style={{ color: '#3C8A5A', flexShrink: 0 }} />
                )}
              </div>
              <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--muted)' }}>
                {meta?.label} · {it.subtitle || it.status}
              </p>
            </div>
            {showDate && (
              <span className="text-xs font-mono flex-shrink-0" style={{ color }}>
                {new Date(it.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            )}
          </Wrap>
        );
      })}
    </div>
  );
}
